import { useEffect, useState } from 'react';
import { SETS } from '../constants';
import { getApiBaseUrl } from '../constants/environment';
import { fetchCatalogRarityCountsForSet } from '../utils/fetchCatalogRarityBySetPages';

export interface CatalogRarityBySet {
  bySet: Record<string, Record<string, number>>;
  /** Cards in each set that have a Simplified Chinese face. */
  zhBySet: Record<string, Record<string, number>>;
  /** Cards in each set that have a Korean face. */
  koBySet: Record<string, Record<string, number>>;
  source: string;
  loading: boolean;
}

type StatsResult = {
  bySet: Record<string, Record<string, number>>;
  zhBySet: Record<string, Record<string, number>>;
  koBySet: Record<string, Record<string, number>>;
  source: string;
};

// ── Module-level session cache ───────────────────────────────────────────────
// Catalog rarity counts only change on catalog syncs, but every mount of
// /collection (each bottom-tab change) used to refetch them and flash the
// "…" / "Overall …" loading states in CollectionStatistics. Cache per set-key
// for the session and dedupe concurrent mounts onto one in-flight promise.
const statsCache = new Map<string, StatsResult>();
const statsInflight = new Map<string, Promise<StatsResult>>();

async function loadCatalogRarityStats(key: string): Promise<StatsResult> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/api/catalog-rarity-stats`);
    const data = res.ok ? await res.json() : null;
    const fromApi = data?.bySet as Record<string, Record<string, number>> | undefined;
    const zhFromApi = data?.zhBySet as Record<string, Record<string, number>> | undefined;
    const koFromApi = data?.koBySet as Record<string, Record<string, number>> | undefined;
    if (fromApi && typeof fromApi === 'object' && Object.keys(fromApi).length > 0) {
      return {
        bySet: fromApi,
        zhBySet: zhFromApi && typeof zhFromApi === 'object' ? zhFromApi : {},
        koBySet: koFromApi && typeof koFromApi === 'object' ? koFromApi : {},
        source: typeof data?.source === 'string' ? data.source : 'cache',
      };
    }

    const sets = key.length > 0 ? key.split(',') : [];
    if (!sets.length) {
      return { bySet: {}, zhBySet: {}, koBySet: {}, source: 'none' };
    }

    const merged: Record<string, Record<string, number>> = {};
    await Promise.all(
      sets.map(async (setCode) => {
        const upper = setCode.toUpperCase();
        merged[upper] = await fetchCatalogRarityCountsForSet(setCode);
      })
    );
    return { bySet: merged, zhBySet: {}, koBySet: {}, source: 'paginated' };
  } catch {
    return { bySet: {}, zhBySet: {}, koBySet: {}, source: 'error' };
  }
}

function loadCatalogRarityStatsOnce(key: string): Promise<StatsResult> {
  const cached = statsCache.get(key);
  if (cached) return Promise.resolve(cached);
  const pending = statsInflight.get(key);
  if (pending) return pending;
  const next = loadCatalogRarityStats(key)
    .then((result) => {
      // Only cache real data — errors and empty fallbacks should retry on
      // the next mount instead of pinning a blank stats panel all session.
      if (result.source !== 'error' && result.source !== 'none') {
        statsCache.set(key, result);
      }
      return result;
    })
    .finally(() => {
      statsInflight.delete(key);
    });
  statsInflight.set(key, next);
  return next;
}

/** Test-only: clear the module cache between tests. */
export function __resetCatalogStatsCacheForTests(): void {
  statsCache.clear();
  statsInflight.clear();
}

function catalogStatsKey(facetSetCodes?: readonly string[]): string {
  const setKeyList =
    facetSetCodes && facetSetCodes.length > 0
      ? [...facetSetCodes]
      : SETS.map((s) => s.code);
  return setKeyList.slice().sort().join(',');
}

/** Warm the session cache before /collection mounts (e.g. after sign-in). */
export function prefetchCollectionCatalogStats(facetSetCodes?: readonly string[]): void {
  void loadCatalogRarityStatsOnce(catalogStatsKey(facetSetCodes));
}

/**
 * Rarity definition counts per set for completion denominators.
 * Prefers Postgres aggregation via /api/catalog-rarity-stats; falls back to paginated /api/cards.
 * Results are cached for the session so route re-mounts render instantly.
 */
export function useCollectionCatalogStats(facetSetCodes: string[]): CatalogRarityBySet {
  const key = catalogStatsKey(facetSetCodes);

  const [state, setState] = useState<CatalogRarityBySet>(() => {
    const cached = statsCache.get(key);
    return cached
      ? { bySet: cached.bySet, zhBySet: cached.zhBySet ?? {}, koBySet: cached.koBySet ?? {}, source: cached.source, loading: false }
      : { bySet: {}, zhBySet: {}, koBySet: {}, source: '', loading: true };
  });

  useEffect(() => {
    const cached = statsCache.get(key);
    if (cached) {
      setState({ bySet: cached.bySet, zhBySet: cached.zhBySet ?? {}, koBySet: cached.koBySet ?? {}, source: cached.source, loading: false });
      return;
    }
    let cancelled = false;
    setState((prev) => (prev.loading ? prev : { ...prev, loading: true }));
    void loadCatalogRarityStatsOnce(key).then((result) => {
      if (cancelled) return;
      setState({
        bySet: result.bySet,
        zhBySet: result.zhBySet ?? {},
        koBySet: result.koBySet ?? {},
        source: result.source,
        loading: false,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return state;
}
